<template>
  <main-template>
    <v-row>
      <v-col>
        <e-help-tooltip :text="$constants.tooltips.turmas.criarAno" />
        <span class="text-h4">Anos</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pb-0 pt-0">
        <div class="d-flex justify-space-between mb-2">
          <v-btn class="mr-2" color="success" @click="criarSerie"> Criar ano </v-btn>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            autofocus
            class="ml-2 search-input"
            dense
            hide-details
            label="Pesquisar..."
            single-line
            solo
          />
        </div>
      </v-col>

      <v-col class="pt-0">
        <v-data-table
          :headers="tableHeaders"
          :items="seriesList"
          :items-per-page="10"
          :loading="isLoadingSeries"
          class="elevation-1"
          hide-default-footer
        >
          <template v-slot:body="{ items }">
            <tbody is="transition-group" duration="150" name="scroll-x-transition">
              <tr
                v-for="serie of items"
                :key="serie.id"
                :class="serie.estaDeletado ? ['deleted'] : []"
                color="primary"
              >
                <td>{{ serie.id }}</td>
                <td>{{ serie.ano ? serie.ano.descricao : "- - -" }}</td>
                <td>{{ serie.descricao }}</td>
                <td>
                  <v-tooltip v-if="!serie.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        color="blue-grey lighten-3"
                        x-small
                        @click="() => editarSerie(serie)"
                      >
                        <v-icon small>fa fa-edit</v-icon>
                        <span class="sr-only">Editar série</span>
                      </v-btn>
                    </template>
                    <span>Editar série</span>
                  </v-tooltip>

                  <v-tooltip v-if="!serie.estaDeletado" bottom color="error">
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        color="error"
                        x-small
                        @click="() => deletarSerie(serie)"
                      >
                        <v-icon small>fa fa-trash</v-icon>
                        <span class="sr-only">Apagar série</span>
                      </v-btn>
                    </template>
                    <span>Apagar série</span>
                  </v-tooltip>

                  <v-tooltip v-if="serie.estaDeletado" bottom>
                    <template v-slot:activator="{ attrs, on }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ml-1"
                        color="info"
                        x-small
                        @click="() => recuperarSerie(serie)"
                      >
                        <v-icon small>fa fa-sync</v-icon>
                        <span class="sr-only">Recuperar série</span>
                      </v-btn>
                    </template>
                    <span>Recuperar série</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>

        <e-paginate
          v-if="paginate.last_page > 1"
          :current_page="paginate.current_page"
          :last_page="paginate.last_page"
          :page-number="pageNumber"
          :search="search"
          @changeActionResponse="listarSeries"
        />
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import Serie from "@/Models/Serie";

export default {
  name: "YearsList",

  data() {
    return {
      isLoadingSeries: false,
      pageNumber: 1,
      paginate: {
        current_page: 1,
        last_page: 0,
      },
      search: "",
      searchDebounce: null,
      seriesList: [],
      tableHeaders: [
        { text: "ID", value: "id" },
        { text: "Ano", value: "ano" },
        { text: "Nome", value: "descricao" },
        { text: "Ações", value: "actions", sortable: false },
      ],
    };
  },

  mounted() {
    this.$store.dispatch("Series/loadSeries");
    this.listarSeries(this.pageNumber);
  },

  watch: {
    isLoadingSeries(value) {
      if (value && this.seriesList.length === 0) {
        this.$loaderService.open("Carregando series");
      } else {
        this.$loaderService.close();
      }
    },

    search(value) {
      clearTimeout(this.searchDebounce);

      this.searchDebounce = setTimeout(() => {
        if (value.length > 2) {
          this.listarSeries(1, value);
        } else if (!value) {
          this.listarSeries(1);
        }
      }, 400);
    },
  },

  methods: {
    async deletarSerie(serie) {
      this.$modals.danger({
        buttons: [
          {
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Deletando a Serie");
              try {
                await this.$services.seriesService.deletarSerie(serie);
                this.$toast.success("Serie deletada com sucesso");
                await this.listarSeries(this.paginate.current_page, this.search || null);
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: serie.id,
        confirmationMessage: `Por favor, digite <strong>${serie.id}</strong> para confirmar`,
        message: "Tem certeza que deseja apagar esta serie?",
        title: "Cuidado!",
      });
    },

    editarSerie(serie) {
      this.$router.push({
        name: "series.edit",
        params: { serie_id: serie.id },
      });
    },

    criarSerie() {
      this.$router.push({ name: "series.create" });
    },

    async listarSeries(pageNumber = 1, query = null) {
      this.isLoadingSeries = true;

      try {
        const response = await this.$services.seriesService.syncAllPaginated(pageNumber, query);

        this.seriesList = response.data.map((serie) => new Serie(serie));
        this.paginate = response;
      } catch (error) {
        this.$handleError(error);
      }

      this.isLoadingSeries = false;
    },

    recuperarSerie(serie) {
      this.$modals.warn({
        buttons: [
          {
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
            text: "Cancelar",
          },
          {
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Recuperando a Serie");
              try {
                await this.$services.seriesService.recuperarSerie(serie);
                this.$toast.success("Serie recuperada com sucesso");
                await this.listarSeries(this.paginate.current_page, this.search || null);
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            disableIfCodeDoesntMatch: true,
            props: {
              color: "error",
              small: true,
            },
            text: "Confirmar",
          },
        ],
        confirmationCode: serie.id,
        confirmationMessage: `Por favor, digite <strong>${serie.id}</strong> para confirmar`,
        message: "Tem certeza que deseja recuperar esta serie?",
        title: "Atenção!",
      });
    },
  },
};
</script>

<style scoped></style>
